import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {InfoIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {pluralize} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../../../app/rp_reducer";
import {useMultiLeadFormSend} from "../../../../../application/hooks/use_multi_lead_form_send";
import {ISetPreferencesStepPayload} from "../../../../../application/types/ISetPreferencesStepPayload";
import {MultiLeadRecommendationType} from "../../../../../application/types/MultiLeadRecommendationType";
import {ApplicationSourceSection} from "../../../../../application/utils/ApplicationSource";
import {getStoreHash} from "../../../../../application/utils/get_store_hash";
import {
    transformRecommendedOfferIntoMultiLeadData,
    transformRecommendedPropertyIntoMultiLeadData
} from "../../../../../application/utils/recommended_application/transform_recommended_into_multi_lead_data";
import {notifyBugsnag} from "../../../../../errors/bugsnag/notify_bugsnag";
import {useGetOfferDetailApplicationQuery} from "../../../../../offer/api/get_offer_detail";
import {IRecommendedOffer} from "../../../../../offer/types/IRecommendedOffer";
import {useGetPropertyDetailApplicationQuery} from "../../../../../property/api/get_property_detail";
import {IRecommendedProperty} from "../../../../../property/types/IRecommendedProperty";
import {useAppSelector} from "../../../../../store/hooks";
import {useInitialLeadFormValues} from "../../../../hooks/use_initial_lead_form_values";
import {useLeadRequiredParams} from "../../../../hooks/use_lead_required_params";

interface IProps {
    recommendations: IRecommendedProperty[] | IRecommendedOffer[] | null;
    // TODO delete or uncomment depending on whether the flow from the task CU-8699ztmzv remains
    //handleSetMultiLeadSummary: (payload: ISetMultiLeadSummaryStepPayload) => void;
    handleSetPreferencesState: (payload: ISetPreferencesStepPayload) => void;
    className?: string;
    storeHash: string;
}
// TODO code to be deleted or used in the future, business does not know whether we ultimately want to abandon multilead
export function LeadModalPriceConversationMultiLeadActions(props: IProps) {
    const {recommendations, handleSetPreferencesState, className} = props;
    const recommendationCount = recommendations?.length || 0;
    const originalApplication = useSelector((store: IRPStore) => store.application[props.storeHash]);

    const {vendorId, offerId, propertyId, sourceSection} = useLeadRequiredParams("property");
    const {data: propertyDetail} = useGetPropertyDetailApplicationQuery(propertyId);
    const {data: offerDetail} = useGetOfferDetailApplicationQuery(offerId);

    const isOpened = useAppSelector((state) => state.mainLead.isModalOpen);
    const initialLeadFormValues = useInitialLeadFormValues({
        realEstateIds: {vendorId, offerId, propertyId},
        leadData: offerDetail ? {offer: {detail: offerDetail}} : null,
        resetValues: !isOpened
    });

    const recommendationType = getRecommendationTypeFromRecommendations(recommendations);
    const {sendMultiLeadForm, isMultiLeadSending} = useMultiLeadFormSend({
        storeHash: getStoreHash({vendorId, offerId, propertyId}),
        recommendationType,
        sourceSection: sourceSection || ApplicationSourceSection.MODAL,
        originalApplicationFormValues: initialLeadFormValues,
        recommendationsWithPriceFilter: false
    });

    const [isSticky, setIsSticky] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const observer = useRef<IntersectionObserver | null>(null);
    const {isMobile} = useUserDevice();

    const handleMultiLeadSkip = () => {
        handleSetPreferencesState({
            originalApplicationResponse: originalApplication?.application || null,
            multiLeadSummaryItems: [],
            offerId,
            propertyId
        });
        // TODO delete or uncomment depending on whether the flow from the task CU-8699ztmzv remains
        /*
        handleSetMultiLeadSummary({
            multiLeadProperty: propertyDetail,
            multiLeadSummaryItems: []
        });
        */
    };

    const handleSendMultiLead = async () => {
        if (!recommendations || !propertyDetail || !initialLeadFormValues) {
            return;
        }

        try {
            const sentApplications = await sendMultiLeadForm({
                ...(recommendationType === MultiLeadRecommendationType.OFFER ? {recommendedOffers: recommendations as IRecommendedOffer[]} : {}),
                ...(recommendationType === MultiLeadRecommendationType.PROPERTY ? {recommendedProperties: recommendations as IRecommendedProperty[]} : {}),
                formValues: initialLeadFormValues
            });

            const multiLeadSummaryItems = sentApplications.map(([_application, appliedRecommendation]) => {
                const isProperty = "offer" in appliedRecommendation;
                return isProperty
                    ? transformRecommendedPropertyIntoMultiLeadData(appliedRecommendation as IRecommendedProperty, {isSelected: true})
                    : transformRecommendedOfferIntoMultiLeadData(appliedRecommendation as IRecommendedOffer, {isSelected: true});
            });

            handleSetPreferencesState({
                originalApplicationResponse: originalApplication?.application || null,
                multiLeadSummaryItems,
                offerId,
                propertyId
            });
            // TODO delete or uncomment depending on whether the flow from the task CU-8699ztmzv remains
            /*
            handleSetMultiLeadSummary({
                multiLeadProperty: propertyDetail,
                multiLeadSummaryItems
            });
            */
        } catch (error) {
            notifyBugsnag("LEAD_MODAL_PRICE_CONVERSATION_SEND_MULTI_LEAD_ERROR", "LEAD_MODAL_PRICE_CONVERSATION_SEND_MULTI_LEAD_ERROR", error);
            handleSetPreferencesState({
                originalApplicationResponse: originalApplication?.application || null,
                multiLeadSummaryItems: [],
                offerId,
                propertyId
            });
            // TODO delete or uncomment depending on whether the flow from the task CU-8699ztmzv remains
            /*
            handleSetMultiLeadSummary({
                multiLeadProperty: propertyDetail,
                multiLeadSummaryItems: []
            });
            */
        }
    };

    useEffect(() => {
        if (isMobile) {
            observer.current = new IntersectionObserver(
                ([entry]) => {
                    setIsSticky(!entry.isIntersecting);
                },
                {
                    root: null,
                    rootMargin: "0px",
                    threshold: 0
                }
            );

            if (containerRef.current) {
                observer.current.observe(containerRef.current);
            }
        }

        return () => {
            if (containerRef.current && observer.current) {
                observer.current.unobserve(containerRef.current);
                observer.current = null;
            }
        };
    }, [isMobile]);

    return (
        <>
            <div ref={containerRef} className={cx(additionalOffersStyles, className)}>
                <Button
                    variant="filled_primary"
                    size="x-small"
                    fullWidth
                    onClick={handleSendMultiLead}
                    isLoading={isMultiLeadSending}
                    disabled={isMultiLeadSending}
                    dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                >
                    {`ZAPYTAJ O ${recommendationCount} ${pluralize(["DODATKOWĄ OFERTĘ", "DODATKOWE OFERTY", "DODATKOWYCH OFERT"])(recommendationCount)}`}
                </Button>

                <div className={infoRowStyles}>
                    <div className={infoRowIconWrapperStyles}>
                        <InfoIcon size="1.6" fill="var(--colors-gray-800)" />
                    </div>
                    <Text variant="info_txt_2" color="var(--colors-gray-800)">
                        Oczekuj na kontakt od deweloperów w ciągu najbliższych 24 godzin
                    </Text>
                </div>

                <Button variant="highlight_primary" size="x-small" className={skipButtonStyles} disabled={isMultiLeadSending} onClick={handleMultiLeadSkip}>
                    POMIŃ
                </Button>
            </div>

            {isSticky && (
                <div className={stickyBarStyles}>
                    <Button
                        variant="filled_primary"
                        size="small"
                        fullWidth
                        onClick={handleSendMultiLead}
                        isLoading={isMultiLeadSending}
                        disabled={isMultiLeadSending}
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                    >
                        {`ZAPYTAJ O ${recommendationCount} ${pluralize(["DODATKOWĄ OFERTĘ", "DODATKOWE OFERTY", "DODATKOWYCH OFERT"])(recommendationCount)}`}
                    </Button>
                </div>
            )}
        </>
    );
}

const getRecommendationTypeFromRecommendations = (recommendations: IRecommendedOffer[] | IRecommendedProperty[] | null) => {
    return recommendations && recommendations.length > 0
        ? "offer" in recommendations[0]
            ? MultiLeadRecommendationType.PROPERTY
            : MultiLeadRecommendationType.OFFER
        : MultiLeadRecommendationType.PROPERTY;
};

const additionalOffersStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    alignItems: "center",
    justifyContent: "flex-start",
    pt: 1.5,
    width: "100%"
});

const infoRowStyles = css({
    display: "flex",
    flexDirection: "row",
    gap: 1,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    width: "100%"
});

const infoRowIconWrapperStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "1.6rem",
    height: "1.6rem"
});

const skipButtonStyles = css({
    my: 1
});

const stickyBarStyles = css({
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    borderTop: "1px solid var(--colors-gray-200)",
    padding: 1.5,
    zIndex: 1000,
    boxShadow: "var(--shadow-4)"
});
import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {InfoIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {pluralize} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../../../app/rp_reducer";
import {useMultiLeadFormSend} from "../../../../../application/hooks/use_multi_lead_form_send";
import {ISetPreferencesStepPayload} from "../../../../../application/types/ISetPreferencesStepPayload";
import {MultiLeadRecommendationType} from "../../../../../application/types/MultiLeadRecommendationType";
import {ApplicationSourceSection} from "../../../../../application/utils/ApplicationSource";
import {getStoreHash} from "../../../../../application/utils/get_store_hash";
import {
    transformRecommendedOfferIntoMultiLeadData,
    transformRecommendedPropertyIntoMultiLeadData
} from "../../../../../application/utils/recommended_application/transform_recommended_into_multi_lead_data";
import {notifyBugsnag} from "../../../../../errors/bugsnag/notify_bugsnag";
import {useGetOfferDetailApplicationQuery} from "../../../../../offer/api/get_offer_detail";
import {IRecommendedOffer} from "../../../../../offer/types/IRecommendedOffer";
import {useGetPropertyDetailApplicationQuery} from "../../../../../property/api/get_property_detail";
import {IRecommendedProperty} from "../../../../../property/types/IRecommendedProperty";
import {useAppSelector} from "../../../../../store/hooks";
import {useInitialLeadFormValues} from "../../../../hooks/use_initial_lead_form_values";
import {useLeadRequiredParams} from "../../../../hooks/use_lead_required_params";

interface IProps {
    recommendations: IRecommendedProperty[] | IRecommendedOffer[] | null;
    // TODO delete or uncomment depending on whether the flow from the task CU-8699ztmzv remains
    //handleSetMultiLeadSummary: (payload: ISetMultiLeadSummaryStepPayload) => void;
    handleSetPreferencesState: (payload: ISetPreferencesStepPayload) => void;
    className?: string;
    storeHash: string;
}
// TODO code to be deleted or used in the future, business does not know whether we ultimately want to abandon multilead
export function LeadModalPriceConversationMultiLeadActions(props: IProps) {
    const {recommendations, handleSetPreferencesState, className} = props;
    const recommendationCount = recommendations?.length || 0;
    const originalApplication = useSelector((store: IRPStore) => store.application[props.storeHash]);

    const {vendorId, offerId, propertyId, sourceSection} = useLeadRequiredParams("property");
    const {data: propertyDetail} = useGetPropertyDetailApplicationQuery(propertyId);
    const {data: offerDetail} = useGetOfferDetailApplicationQuery(offerId);

    const isOpened = useAppSelector((state) => state.mainLead.isModalOpen);
    const initialLeadFormValues = useInitialLeadFormValues({
        realEstateIds: {vendorId, offerId, propertyId},
        leadData: offerDetail ? {offer: {detail: offerDetail}} : null,
        resetValues: !isOpened
    });

    const recommendationType = getRecommendationTypeFromRecommendations(recommendations);
    const {sendMultiLeadForm, isMultiLeadSending} = useMultiLeadFormSend({
        storeHash: getStoreHash({vendorId, offerId, propertyId}),
        recommendationType,
        sourceSection: sourceSection || ApplicationSourceSection.MODAL,
        originalApplicationFormValues: initialLeadFormValues,
        recommendationsWithPriceFilter: false
    });

    const [isSticky, setIsSticky] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const observer = useRef<IntersectionObserver | null>(null);
    const {isMobile} = useUserDevice();

    const handleMultiLeadSkip = () => {
        handleSetPreferencesState({
            originalApplicationResponse: originalApplication?.application || null,
            multiLeadSummaryItems: [],
            offerId,
            propertyId
        });
        // TODO delete or uncomment depending on whether the flow from the task CU-8699ztmzv remains
        /*
        handleSetMultiLeadSummary({
            multiLeadProperty: propertyDetail,
            multiLeadSummaryItems: []
        });
        */
    };

    const handleSendMultiLead = async () => {
        if (!recommendations || !propertyDetail || !initialLeadFormValues) {
            return;
        }

        try {
            const sentApplications = await sendMultiLeadForm({
                ...(recommendationType === MultiLeadRecommendationType.OFFER ? {recommendedOffers: recommendations as IRecommendedOffer[]} : {}),
                ...(recommendationType === MultiLeadRecommendationType.PROPERTY ? {recommendedProperties: recommendations as IRecommendedProperty[]} : {}),
                formValues: initialLeadFormValues
            });

            const multiLeadSummaryItems = sentApplications.map(([_application, appliedRecommendation]) => {
                const isProperty = "offer" in appliedRecommendation;
                return isProperty
                    ? transformRecommendedPropertyIntoMultiLeadData(appliedRecommendation as IRecommendedProperty, {isSelected: true})
                    : transformRecommendedOfferIntoMultiLeadData(appliedRecommendation as IRecommendedOffer, {isSelected: true});
            });

            handleSetPreferencesState({
                originalApplicationResponse: originalApplication?.application || null,
                multiLeadSummaryItems,
                offerId,
                propertyId
            });
            // TODO delete or uncomment depending on whether the flow from the task CU-8699ztmzv remains
            /*
            handleSetMultiLeadSummary({
                multiLeadProperty: propertyDetail,
                multiLeadSummaryItems
            });
            */
        } catch (error) {
            notifyBugsnag("LEAD_MODAL_PRICE_CONVERSATION_SEND_MULTI_LEAD_ERROR", "LEAD_MODAL_PRICE_CONVERSATION_SEND_MULTI_LEAD_ERROR", error);
            handleSetPreferencesState({
                originalApplicationResponse: originalApplication?.application || null,
                multiLeadSummaryItems: [],
                offerId,
                propertyId
            });
            // TODO delete or uncomment depending on whether the flow from the task CU-8699ztmzv remains
            /*
            handleSetMultiLeadSummary({
                multiLeadProperty: propertyDetail,
                multiLeadSummaryItems: []
            });
            */
        }
    };

    useEffect(() => {
        if (isMobile) {
            observer.current = new IntersectionObserver(
                ([entry]) => {
                    setIsSticky(!entry.isIntersecting);
                },
                {
                    root: null,
                    rootMargin: "0px",
                    threshold: 0
                }
            );

            if (containerRef.current) {
                observer.current.observe(containerRef.current);
            }
        }

        return () => {
            if (containerRef.current && observer.current) {
                observer.current.unobserve(containerRef.current);
                observer.current = null;
            }
        };
    }, [isMobile]);

    return (
        <>
            <div ref={containerRef} className={cx(additionalOffersStyles, className)}>
                <Button
                    variant="filled_primary"
                    size="x-small"
                    fullWidth
                    onClick={handleSendMultiLead}
                    isLoading={isMultiLeadSending}
                    disabled={isMultiLeadSending}
                    dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                >
                    {`ZAPYTAJ O ${recommendationCount} ${pluralize(["DODATKOWĄ OFERTĘ", "DODATKOWE OFERTY", "DODATKOWYCH OFERT"])(recommendationCount)}`}
                </Button>

                <div className={infoRowStyles}>
                    <div className={infoRowIconWrapperStyles}>
                        <InfoIcon size="1.6" fill="var(--colors-gray-800)" />
                    </div>
                    <Text variant="info_txt_2" color="var(--colors-gray-800)">
                        Oczekuj na kontakt od deweloperów w ciągu najbliższych 24 godzin
                    </Text>
                </div>

                <Button variant="highlight_primary" size="x-small" className={skipButtonStyles} disabled={isMultiLeadSending} onClick={handleMultiLeadSkip}>
                    POMIŃ
                </Button>
            </div>

            {isSticky && (
                <div className={stickyBarStyles}>
                    <Button
                        variant="filled_primary"
                        size="small"
                        fullWidth
                        onClick={handleSendMultiLead}
                        isLoading={isMultiLeadSending}
                        disabled={isMultiLeadSending}
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                    >
                        {`ZAPYTAJ O ${recommendationCount} ${pluralize(["DODATKOWĄ OFERTĘ", "DODATKOWE OFERTY", "DODATKOWYCH OFERT"])(recommendationCount)}`}
                    </Button>
                </div>
            )}
        </>
    );
}

const getRecommendationTypeFromRecommendations = (recommendations: IRecommendedOffer[] | IRecommendedProperty[] | null) => {
    return recommendations && recommendations.length > 0
        ? "offer" in recommendations[0]
            ? MultiLeadRecommendationType.PROPERTY
            : MultiLeadRecommendationType.OFFER
        : MultiLeadRecommendationType.PROPERTY;
};

const additionalOffersStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    alignItems: "center",
    justifyContent: "flex-start",
    pt: 1.5,
    width: "100%"
});

const infoRowStyles = css({
    display: "flex",
    flexDirection: "row",
    gap: 1,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    width: "100%"
});

const infoRowIconWrapperStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "1.6rem",
    height: "1.6rem"
});

const skipButtonStyles = css({
    my: 1
});

const stickyBarStyles = css({
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    borderTop: "1px solid var(--colors-gray-200)",
    padding: 1.5,
    zIndex: 1000,
    boxShadow: "var(--shadow-4)"
});
